import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPinIcon, MailIcon, PhoneIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const CONTACT_ACCESS_KEY = 'ad204ca0-3dd1-4998-b6cd-8da8231b1634';
export function Contact() {
  useSEO({
    title: 'Contact Us',
    description: 'Contact 888 Sourcing Ltd, London-based procurement consultancy. Get in touch for institutional sourcing, supplier coordination, and tender support enquiries across global markets.',
    path: '/contact'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    subject: '',
    message: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: CONTACT_ACCESS_KEY,
          subject: `New contact enquiry: ${formData.subject}`,
          from_name: '888 Sourcing Website',
          botcheck: '',
          ...formData
        })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.success) {
        setIsSuccess(true);
      } else {
        setErrorMessage(data?.message || 'Something went wrong. Please try again or email us directly at info@888sourcing.co.uk.');
      }
    } catch (err) {
      setErrorMessage('Unable to submit at the moment. Please check your connection or email us directly at info@888sourcing.co.uk.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="pt-24 pb-24 bg-navy-dark min-h-screen" data-id="element-374">
      {/* Hero Band */}
      <section className="py-16 px-6 bg-navy-light border-b border-slate-800" data-id="element-375">
        <div className="max-w-4xl mx-auto text-center" data-id="element-376">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-377">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-378">
              <div className="w-12 h-px bg-gold" data-id="element-379"></div>
              <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-380">
                Get in Touch
              </span>
              <div className="w-12 h-px bg-gold" data-id="element-381"></div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-semibold text-white mb-6" data-id="element-382">
              Contact Our Team
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed" data-id="element-383">
              Whether you are a procurement entity seeking sourcing support or a
              supplier looking to access global markets, we are ready to assist.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6" data-id="element-384">
        <div className="max-w-7xl mx-auto" data-id="element-385">
          <div className="grid lg:grid-cols-2 gap-12" data-id="element-386">
            {/* Left: Contact Info */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-387">
              <div className="bg-navy-light border-l-4 border-gold p-8 rounded-lg h-full" data-id="element-388">
                <h2 className="text-2xl font-semibold text-white mb-8" data-id="element-389">
                  Headquarters
                </h2>

                <div className="space-y-8" data-id="element-390">
                  <div className="flex items-start gap-4" data-id="element-391">
                    <div className="w-12 h-12 bg-navy-dark border border-slate-800 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-392">
                      <MapPinIcon size={20} className="text-gold" data-id="element-393" />
                    </div>
                    <div data-id="element-394">
                      <h3 className="text-white font-medium mb-1" data-id="element-395">
                        Registered Office
                      </h3>
                      <p className="text-slate-400 leading-relaxed" data-id="element-396">
                        888 Sourcing Ltd
                        <br data-id="element-397" />
                        Vermeulens Garden Centre, Horton Road
                        <br data-id="element-398" />
                        Staines-upon-Thames, Middlesex TW19 6AE
                        <br data-id="element-399" />
                        United Kingdom
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-400">
                    <div className="w-12 h-12 bg-navy-dark border border-slate-800 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-401">
                      <MailIcon size={20} className="text-gold" data-id="element-402" />
                    </div>
                    <div data-id="element-403">
                      <h3 className="text-white font-medium mb-1" data-id="element-404">Email</h3>
                      <a href="mailto:info@888sourcing.co.uk" className="text-slate-400 hover:text-gold transition-colors" data-id="element-405">
                        info@888sourcing.co.uk
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-406">
                    <div className="w-12 h-12 bg-navy-dark border border-slate-800 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-407">
                      <PhoneIcon size={20} className="text-gold" data-id="element-408" />
                    </div>
                    <div data-id="element-409">
                      <h3 className="text-white font-medium mb-1" data-id="element-410">Telephone</h3>
                      <a href="tel:+443330579291" className="text-slate-400 hover:text-gold transition-colors" data-id="element-411">
                        +44 333 057 9291
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-412">
                    <div className="w-12 h-12 bg-navy-dark border border-slate-800 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-413">
                      <ClockIcon size={20} className="text-gold" data-id="element-414" />
                    </div>
                    <div data-id="element-415">
                      <h3 className="text-white font-medium mb-1" data-id="element-416">
                        Business Hours
                      </h3>
                      <p className="text-slate-400" data-id="element-417">
                        Monday - Friday
                        <br data-id="element-418" />
                        09:00 - 17:30 (GMT)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 pt-8 border-t border-slate-800" data-id="element-419">
                  <h3 className="text-white font-medium mb-2" data-id="element-420">
                    Company Information
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-421">
                    Company Registration No: 08400329
                    <br data-id="element-422" />
                    VAT Registration No: GB158689350
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right: Contact Form */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} data-id="element-423">
              {isSuccess ? <div className="bg-navy-light border border-slate-800 p-8 rounded-lg h-full flex flex-col items-center justify-center text-center" data-id="element-424">
                  <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mb-6" data-id="element-425">
                    <CheckCircleIcon size={32} className="text-green-500" data-id="element-426" />
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-427">
                    Message Sent
                  </h2>
                  <p className="text-slate-400 leading-relaxed mb-8" data-id="element-428">
                    Thank you for contacting 888 Sourcing Ltd. A member of our
                    team will respond to your inquiry shortly.
                  </p>
                  <button onClick={() => {
                setIsSuccess(false);
                setFormData({
                  fullName: '',
                  company: '',
                  email: '',
                  subject: '',
                  message: ''
                });
              }} className="bg-navy-dark border border-slate-700 hover:border-gold text-white px-6 py-2.5 rounded-md font-semibold text-sm transition-colors" data-id="element-429">
                    Send Another Message
                  </button>
                </div> : <div className="bg-navy-light border border-slate-800 p-8 rounded-lg" data-id="element-430">
                  <h2 className="text-2xl font-semibold text-white mb-6" data-id="element-431">
                    Send an Inquiry
                  </h2>
                  {errorMessage && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-4 rounded-md mb-6" data-id="element-432">
                      {errorMessage}
                    </div>}
                  <form onSubmit={handleSubmit} className="space-y-6" data-id="element-433">
                    <div className="grid sm:grid-cols-2 gap-6" data-id="element-434">
                      <div data-id="element-435">
                        <label htmlFor="fullName" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-436">
                          Full Name *
                        </label>
                        <input id="fullName" name="fullName" required type="text" value={formData.fullName} onChange={handleChange} className="w-full bg-navy-dark border border-slate-700 rounded-md px-4 py-2.5 text-white focus:outline-none focus:border-gold transition-colors" data-id="element-437" />
                      </div>
                      <div data-id="element-438">
                        <label htmlFor="company" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-439">
                          Company
                        </label>
                        <input id="company" name="company" type="text" value={formData.company} onChange={handleChange} className="w-full bg-navy-dark border border-slate-700 rounded-md px-4 py-2.5 text-white focus:outline-none focus:border-gold transition-colors" data-id="element-440" />
                      </div>
                    </div>
                    <div data-id="element-441">
                      <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-442">
                        Email Address *
                      </label>
                      <input id="email" name="email" required type="email" value={formData.email} onChange={handleChange} className="w-full bg-navy-dark border border-slate-700 rounded-md px-4 py-2.5 text-white focus:outline-none focus:border-gold transition-colors" data-id="element-443" />
                    </div>
                    <div data-id="element-444">
                      <label htmlFor="subject" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-445">
                        Subject *
                      </label>
                      <input id="subject" name="subject" required type="text" value={formData.subject} onChange={handleChange} className="w-full bg-navy-dark border border-slate-700 rounded-md px-4 py-2.5 text-white focus:outline-none focus:border-gold transition-colors" data-id="element-446" />
                    </div>
                    <div data-id="element-447">
                      <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-448">
                        Message *
                      </label>
                      <textarea id="message" name="message" required rows={5} value={formData.message} onChange={handleChange} className="w-full bg-navy-dark border border-slate-700 rounded-md px-4 py-2.5 text-white focus:outline-none focus:border-gold transition-colors" data-id="element-449"></textarea>
                    </div>
                    <button type="submit" disabled={isSubmitting} className="w-full bg-gold hover:bg-gold-dark disabled:bg-gold/50 disabled:cursor-not-allowed text-navy-dark px-6 py-3.5 rounded-md font-semibold text-sm transition-colors" data-id="element-450">
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                </div>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 px-6" data-id="element-451">
        <div className="max-w-4xl mx-auto text-center" data-id="element-452">
          <div className="bg-navy-light border border-slate-800 p-8 rounded-lg" data-id="element-453">
            <h3 className="text-xl font-semibold text-white mb-3" data-id="element-454">
              Looking to become a supplier?
            </h3>
            <p className="text-slate-400 mb-6" data-id="element-455">
              Please complete our pre-qualification form to submit your company
              details and capabilities.
            </p>
            <Link to="/supplier-registration" className="inline-block border-2 border-gold text-gold hover:bg-gold hover:text-navy-dark px-6 py-2.5 rounded-md font-semibold text-sm transition-colors" data-id="element-456">
              Open Pre-Qualification Form →
            </Link>
          </div>
        </div>
      </section>
    </div>;
}